import Add from '@mui/icons-material/Add';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import React, { useState, useEffect } from 'react';

import { CapacityListProps } from './CapacityList.props';
import { CapacityBox, StructureCapacityAddGrid, StyledTypography } from './CapacityList.styles';
import { SizeProps } from '../../../@types';
import { AddRowButton } from '../../atoms/AddRowButton';
import { Button, ButtonVariantProps } from '../../atoms/Button';
import { TypographyVariantProps } from '../../atoms/Typography';
import { Capacity, CapacityData } from '../../organisms/Capacity';

export const CapacityList: React.FC<CapacityListProps> = ({
  heading,
  capacityValue,
  capacityDefaultValue,
  hasCapacityEditPermission = true,
  onCapacityCreate,
  onCapacityChange,
  onCapacityDelete,
  flush,
}) => {
  const [capacityData, setCapacityData] = useState<CapacityData[]>(capacityValue);

  useEffect(() => {
    capacityValue && setCapacityData(capacityValue);
  }, [capacityValue]);

  const addNewForm = () => {
    setCapacityData([...capacityValue, capacityDefaultValue]);
    onCapacityCreate && onCapacityCreate([...capacityValue, capacityDefaultValue]);
  };

  const handleCapacityChange = (updatedCapacityData: CapacityData, index: number) => {
    const newCapacityData = capacityData.map((capacityDataItem, itemIndex) =>
      index === itemIndex ? updatedCapacityData : capacityDataItem
    );

    setCapacityData(newCapacityData);
    onCapacityChange(newCapacityData);
  };

  const handleCapacityDelete = (index: number) => {
    onCapacityDelete(index);
    const updatedCapacityData = [...capacityData];
    index > -1 && updatedCapacityData.splice(index, 1);
    setCapacityData([...updatedCapacityData]);
  };

  const rows = capacityData?.map((capacityDataItem, index) => (
    <Capacity
      key={index}
      capacityData={capacityDataItem}
      onChange={(updatedData) => handleCapacityChange(updatedData, index)}
      onDelete={() => handleCapacityDelete(index)}
      hasCapacityEditPermission={hasCapacityEditPermission}
    />
  ));

  // See `TradingHourList` — flush callers sit inside a v4 `Panel`, where a
  // `Paper` table is a card drawn inside a card.
  if (flush) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows?.map((row, index) => (
          <Box key={index} sx={{ py: 1, borderTop: index > 0 ? 1 : 0, borderColor: 'v4.border' }}>
            {row}
          </Box>
        ))}
        {hasCapacityEditPermission && (
          <AddRowButton
            label="Add capacity"
            onClick={addNewForm}
            showDivider={(rows?.length ?? 0) > 0}
          />
        )}
      </Box>
    );
  }

  return (
    <CapacityBox flush={flush}>
      <Grid container>
        <Grid container justifyContent="space-between" mx={flush ? 0 : 1} flex={1}>
          <Grid>
            {heading && !flush && (
              <StyledTypography variant={TypographyVariantProps.H6}>{heading}</StyledTypography>
            )}
          </Grid>
          {hasCapacityEditPermission && (
            <StructureCapacityAddGrid>
              <Button
                variant={ButtonVariantProps.Secondary}
                startIcon={<Add />}
                onClick={() => addNewForm()}
              >
                Add
              </Button>
            </StructureCapacityAddGrid>
          )}
        </Grid>
        <TableContainer component={Paper}>
          <Table size={SizeProps.Small}>
            <TableBody>
              {rows?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Grid pt="5px">{row}</Grid>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </CapacityBox>
  );
};
